import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ManagedNumber, ManagedNumberOrderRow, OrderSummary, Product } from '@/api/types';
import { Card, ErrorNote, Money, Reference, Spinner, Timestamp } from '@/components/Common';
import { Modal } from '@/components/Modal';
import { StatusPill } from '@/components/StatusPill';
import { money } from '@/lib/format';

/** A company's own saved numbers — CRUD against GET/POST/PATCH/DELETE /api/v1/numbers, a
 * per-number recharge history (GET /api/v1/numbers/{id}/orders), and bulk recharge across a
 * selection of them (one product/amount applied to every selected number). */
export function ManagedNumbersPage() {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ManagedNumber | null>(null);
  const [deleting, setDeleting] = useState<ManagedNumber | null>(null);
  const [historyFor, setHistoryFor] = useState<ManagedNumber | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkRecharging, setBulkRecharging] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['managed-numbers'],
    queryFn: () => api.get<{ numbers: ManagedNumber[] }>('/api/v1/numbers'),
  });

  const numbers = (data?.numbers ?? []).filter((n) => showInactive || n.active);
  const selectedNumbers = numbers.filter((n) => selected.has(n.id));
  const allSelected = numbers.length > 0 && numbers.every((n) => selected.has(n.id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(numbers.map((n) => n.id)));
  }

  return (
    <div className="space-y-4">
      <Card
        title={`Your numbers — ${numbers.length}`}
        actions={
          <div className="flex items-center gap-3">
            {selected.size > 0 && (
              <button type="button" className="btn-primary" onClick={() => setBulkRecharging(true)}>
                Bulk recharge ({selected.size})
              </button>
            )}
            <label className="flex items-center gap-1.5 text-xs text-ink-600">
              <input type="checkbox" checked={showInactive}
                     onChange={(e) => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
            <button type="button" className="btn-secondary" onClick={() => setCreating(true)}>
              Add number
            </button>
          </div>
        }
      >
        {isLoading && <Spinner />}
        {error && <ErrorNote error={error} />}
        {data && (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-ink-200">
              <tr>
                <th className="th w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
                <th className="th">Label</th><th className="th">Number</th>
                <th className="th">Notes</th><th className="th">Status</th>
                <th className="th">Last recharge</th>
                <th className="th" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {numbers.map((n) => (
                <tr key={n.id} className={n.active ? '' : 'bg-ink-50 text-ink-400'}>
                  <td className="td">
                    <input type="checkbox" checked={selected.has(n.id)} onChange={() => toggle(n.id)} />
                  </td>
                  <td className="td font-medium">{n.label}</td>
                  <td className="td mono">{n.target}</td>
                  <td className="td max-w-xs truncate text-ink-600">{n.notes ?? '—'}</td>
                  <td className="td">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                      n.active ? 'bg-emerald-100 text-emerald-800' : 'bg-ink-200 text-ink-600'
                    }`}>{n.active ? 'ACTIVE' : 'INACTIVE'}</span>
                  </td>
                  <td className="td">
                    {n.lastRechargeAt ? (
                      <div className="flex items-center gap-1.5 text-xs">
                        <StatusPill status={n.lastRechargeStatus!} />
                        <Timestamp value={n.lastRechargeAt} />
                      </div>
                    ) : (
                      <span className="text-xs text-ink-400">Never</span>
                    )}
                  </td>
                  <td className="td text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/recharges/new?target=${encodeURIComponent(n.target)}`}
                            className="btn-secondary">
                        Recharge
                      </Link>
                      <button type="button" className="btn-secondary" onClick={() => setHistoryFor(n)}>
                        History
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => setEditing(n)}>
                        Edit
                      </button>
                      <button type="button" className="btn-secondary text-red-700"
                              onClick={() => setDeleting(n)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {numbers.length === 0 && (
                <tr><td className="td py-8 text-center text-ink-500" colSpan={7}>
                  No numbers saved yet — add the ones you recharge regularly.
                </td></tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      {creating && <NumberModal title="Add a number" onClose={() => setCreating(false)} />}
      {editing && <NumberModal title={`Edit ${editing.label}`} number={editing} onClose={() => setEditing(null)} />}
      {deleting && <DeleteModal number={deleting} onClose={() => setDeleting(null)} />}
      {historyFor && <HistoryModal number={historyFor} onClose={() => setHistoryFor(null)} />}
      {bulkRecharging && (
        <BulkRechargeModal
          numbers={selectedNumbers}
          onClose={() => { setBulkRecharging(false); setSelected(new Set()); }}
        />
      )}
    </div>
  );
}

function NumberModal({ title, number, onClose }: {
  title: string; number?: ManagedNumber; onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState(number?.target ?? '');
  const [label, setLabel] = useState(number?.label ?? '');
  const [notes, setNotes] = useState(number?.notes ?? '');

  const save = useMutation({
    mutationFn: () => number
      ? api.patch<ManagedNumber>(`/api/v1/numbers/${number.id}`,
          { target: number.target, label, notes: notes || undefined, active: number.active })
      : api.post<ManagedNumber>('/api/v1/numbers', { target, label, notes: notes || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-numbers'] });
      onClose();
    },
  });

  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-3">
        <label className="block">
          <span className="label">Number</span>
          <input className="input mt-1" value={target} placeholder="0772929223"
                 disabled={!!number}
                 onChange={(e) => setTarget(e.target.value)} />
          {number && (
            <span className="mt-1 block text-xs text-ink-500">
              The number itself can't be changed — delete and re-add it instead.
            </span>
          )}
        </label>
        <label className="block">
          <span className="label">Label</span>
          <input className="input mt-1" value={label} placeholder="e.g. Delivery van 3"
                 onChange={(e) => setLabel(e.target.value)} />
        </label>
        <label className="block">
          <span className="label">Notes (optional)</span>
          <input className="input mt-1" value={notes}
                 onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>

      {save.isError && <ErrorNote error={save.error} />}

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" disabled={!target || !label || save.isPending}
                onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

function DeleteModal({ number, onClose }: { number: ManagedNumber; onClose: () => void }) {
  const queryClient = useQueryClient();
  const del = useMutation({
    mutationFn: () => api.delete(`/api/v1/numbers/${number.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managed-numbers'] });
      onClose();
    },
  });
  return (
    <Modal title={`Delete ${number.label}?`} onClose={onClose}>
      <p className="text-sm text-ink-600">
        This removes <span className="mono">{number.target}</span> from your saved numbers. Its
        past recharges stay in your order history either way — only the saved label goes away.
      </p>
      {del.isError && <ErrorNote error={del.error} />}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-danger" disabled={del.isPending}
                onClick={() => del.mutate()}>
          {del.isPending ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </Modal>
  );
}

function HistoryModal({ number, onClose }: { number: ManagedNumber; onClose: () => void }) {
  const { data: history, isLoading, error } = useQuery({
    queryKey: ['managed-number-orders', number.id],
    queryFn: () => api.get<{ orders: ManagedNumberOrderRow[] }>(`/api/v1/numbers/${number.id}/orders`),
  });
  return (
    <Modal title={`Recharge history — ${number.label}`} onClose={onClose}>
      {isLoading && <Spinner />}
      {error && <ErrorNote error={error} />}
      {history && (
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-ink-200">
            <tr>
              <th className="th">Reference</th><th className="th">Status</th>
              <th className="th text-right">Amount</th><th className="th">Placed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {history.orders.map((o) => (
              <tr key={o.id}>
                <td className="td"><Link to={`/orders/${o.id}`} className="hover:underline">
                  <Reference value={o.agentReference} /></Link></td>
                <td className="td"><StatusPill status={o.status} /></td>
                <td className="td text-right">
                  <Money amount={money(o.amount)} currency={o.currency} />
                </td>
                <td className="td"><Timestamp value={o.createdAt} /></td>
              </tr>
            ))}
            {history.orders.length === 0 && (
              <tr><td className="td py-6 text-center text-ink-500" colSpan={4}>
                No recharges against this number yet.
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      )}
    </Modal>
  );
}

type BulkResult = { number: ManagedNumber; status: 'pending' | 'done' | 'error'; message?: string };

/**
 * One product + one amount applied to every selected number, submitted as a sequence of ordinary
 * POST /api/v1/recharges calls — not a separate bulk endpoint. Sequential, not parallel: each
 * call must see the previous ones' spend so the daily-limit check (OrderOrchestrator) stays
 * correct, and a failure on one number must never look like it affected any other.
 */
function BulkRechargeModal({ numbers, onClose }: { numbers: ManagedNumber[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState('');
  const [amount, setAmount] = useState('');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<BulkResult[] | null>(null);

  const { data: catalogue, isLoading: loadingCatalogue } = useQuery({
    queryKey: ['catalogue'],
    queryFn: () => api.get<{ products: Product[] }>('/api/admin/catalogue/products'),
  });
  // Bundles need a per-product productCode, which defeats "one amount for everyone" — bulk
  // recharge is for plain top-ups (airtime, ZESA, Telone), not bundle selection.
  const products = (catalogue?.products ?? []).filter((p) => p.enabled && !p.requiresProductCode);
  const selectedProduct = products.find((p) => String(p.productId) === productId);

  const canStart = !!productId && !!amount && !running && results === null;
  const done = results?.filter((r) => r.status !== 'pending').length ?? 0;
  const succeeded = results?.filter((r) => r.status === 'done').length ?? 0;
  const failed = results?.filter((r) => r.status === 'error').length ?? 0;

  async function start() {
    setRunning(true);
    const initial: BulkResult[] = numbers.map((n) => ({ number: n, status: 'pending' }));
    setResults(initial);

    for (let i = 0; i < numbers.length; i++) {
      const target = numbers[i].target;
      try {
        await api.post<OrderSummary>('/api/v1/recharges', {
          productId: Number(productId), target, amount,
          clientReference: `bulk-${Date.now()}-${numbers[i].id}`,
        });
        setResults((prev) => prev!.map((r, idx) => idx === i ? { ...r, status: 'done' } : r));
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed';
        setResults((prev) => prev!.map((r, idx) => idx === i ? { ...r, status: 'error', message: msg } : r));
      }
    }
    setRunning(false);
    queryClient.invalidateQueries({ queryKey: ['managed-numbers'] });
  }

  return (
    <Modal title={`Bulk recharge — ${numbers.length} number${numbers.length === 1 ? '' : 's'}`} onClose={onClose}>
      {results === null ? (
        <>
          <div className="space-y-3">
            <label className="block">
              <span className="label">Product</span>
              <select className="input mt-1" value={productId} disabled={loadingCatalogue}
                      onChange={(e) => setProductId(e.target.value)}>
                <option value="">Select a product…</option>
                {products.map((p) => (
                  <option key={p.productId} value={p.productId}>
                    {p.name}{p.network ? ` (${p.network})` : ''}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-ink-500">
                Bundles aren't available for bulk recharge — one amount applies to every number.
              </span>
            </label>
            <label className="block">
              <span className="label">Amount{selectedProduct?.currency ? ` (${selectedProduct.currency})` : ''}</span>
              <input className="input mt-1 tabular" inputMode="decimal" value={amount}
                     placeholder={selectedProduct?.minAmount ?? '0.00'}
                     onChange={(e) => setAmount(e.target.value)} />
            </label>
          </div>
          <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto rounded border border-ink-100 p-2 text-xs text-ink-600">
            {numbers.map((n) => <li key={n.id}>{n.label} — <span className="mono">{n.target}</span></li>)}
          </ul>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary" disabled={!canStart} onClick={start}>
              Recharge {numbers.length} number{numbers.length === 1 ? '' : 's'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-ink-600">
            {done} of {numbers.length} done — {succeeded} succeeded
            {failed > 0 && <span className="text-red-700">, {failed} failed</span>}.
          </p>
          <ul className="mt-3 max-h-80 space-y-1 overflow-y-auto">
            {results.map((r) => (
              <li key={r.number.id} className="flex items-center justify-between rounded border border-ink-100 px-2 py-1.5 text-sm">
                <div>
                  <span className="font-medium">{r.number.label}</span>
                  <span className="ml-2 mono text-xs text-ink-500">{r.number.target}</span>
                </div>
                {r.status === 'pending' && <span className="text-xs text-ink-400">…</span>}
                {r.status === 'done' && <span className="text-xs font-semibold text-emerald-700">Placed</span>}
                {r.status === 'error' && (
                  <span className="text-xs font-semibold text-red-700" title={r.message}>Failed</span>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-4 flex justify-end">
            <button type="button" className="btn-primary" disabled={running} onClick={onClose}>
              {running ? 'Working…' : 'Done'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
